import React, { useMemo } from 'react'
import DOMPurify from 'dompurify'

interface TextContentProps {
  content: string
}

// Configure DOMPurify to allow safe HTML elements and styles
const sanitizeConfig = {
  ALLOWED_TAGS: [
    // Text formatting
    'p', 'br', 'span', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'del', 'ins', 'sub', 'sup', 'mark',
    // Headings
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    // Lists
    'ul', 'ol', 'li',
    // Links and media
    'a', 'img',
    // Block elements
    'div', 'blockquote', 'pre', 'code',
    // Tables
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    // Horizontal rule
    'hr',
  ],
  ALLOWED_ATTR: [
    'href', 'title', 'target', 'rel',
    'src', 'alt', 'width', 'height',
    'class', 'style',
    'colspan', 'rowspan',
  ],
  ALLOWED_CSS: {
    'text-align': ['left', 'center', 'right', 'justify'],
    'margin': true,
    'margin-left': true,
    'margin-right': true,
    'margin-top': true,
    'margin-bottom': true,
    'padding': true,
    'padding-left': true,
    'padding-right': true,
    'padding-top': true,
    'padding-bottom': true,
    'width': true,
    'max-width': true,
    'height': true,
    'display': true,
    'font-size': true,
  },
  ALLOW_DATA_ATTR: false,
  ADD_ATTR: ['target'],
  FORCE_BODY: false,
}

export const TextContent: React.FC<TextContentProps> = ({ content }) => {
  // Sanitize content to prevent XSS attacks
  const sanitizedContent = useMemo(() => {
    if (!content) return ''

    // Check if content is plain text (no HTML tags) or HTML
    const hasHTMLTags = /<[^>]+>/g.test(content)

    if (!hasHTMLTags) {
      // Plain text: wrap in <p> tag with white-space preservation
      // This handles content_format="text" lessons
      const escapedText = content
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
      return `<p style="white-space: pre-wrap;">${escapedText}</p>`
    }

    // HTML content: sanitize normally
    return DOMPurify.sanitize(content, sanitizeConfig)
  }, [content])

  // Show empty state if no content after sanitization
  if (!sanitizedContent || sanitizedContent.trim() === '') {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-500">
        <span className="material-symbols-outlined text-6xl mb-4">article</span>
        <p className="text-lg">No content available</p>
      </div>
    )
  }

  return (
    <>
      <div
        className="w-full min-h-[200px] bg-white dark:bg-surface-card rounded-lg border border-gray-200 dark:border-white/5 p-5 sm:p-8
          prose prose-invert prose-lg max-w-none text-content-display
          prose-headings:text-gray-900 dark:text-white prose-headings:font-bold
          prose-p:text-gray-900 dark:text-white prose-p:leading-relaxed prose-p:mb-4
          prose-a:text-primary prose-a:no-underline hover:prose-a:underline
          prose-strong:text-gray-900 dark:text-white
          prose-ul:text-gray-900 dark:text-white prose-ol:text-gray-900 dark:text-white
          prose-li:text-gray-900 dark:text-white prose-li:marker:text-primary
          prose-blockquote:border-primary prose-blockquote:text-gray-600 dark:text-gray-300
          prose-img:rounded-lg prose-img:shadow-lg
          [&_p]:whitespace-pre-wrap [&_h1]:whitespace-pre-wrap [&_h2]:whitespace-pre-wrap [&_h3]:whitespace-pre-wrap
          [&_p]:text-gray-900 dark:text-white [&_span]:text-gray-900 dark:text-white [&_div]:text-gray-900 dark:text-white"
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
      />
      <style>{`
        /* Heading Sizes */
        .text-content-display h1 {
          font-size: 2.25em !important;
          line-height: 1.2 !important;
          margin-top: 0.8em !important;
          margin-bottom: 0.4em !important;
          font-weight: 800 !important;
        }
        .text-content-display h2 {
          font-size: 1.875em !important;
          line-height: 1.3 !important;
          margin-top: 0.75em !important;
          margin-bottom: 0.35em !important;
          font-weight: 700 !important;
        }
        .text-content-display h3 {
          font-size: 1.5em !important;
          line-height: 1.4 !important;
          margin-top: 0.7em !important;
          margin-bottom: 0.3em !important;
          font-weight: 700 !important;
        }

        /* Lists Styling - CRITICAL FIX */
        .text-content-display ul {
          list-style-type: disc !important;
          margin-top: 1em !important;
          margin-bottom: 1em !important;
          padding-left: 1.5em !important;
          display: block !important;
        }
        .text-content-display ol {
          list-style-type: decimal !important;
          margin-top: 1em !important;
          margin-bottom: 1em !important;
          padding-left: 1.5em !important;
          display: block !important;
        }
        .text-content-display li {
          margin-bottom: 0.5em !important;
          display: list-item !important;
          line-height: 1.6 !important;
        }

        /* Image Styling & Alignment */
        .text-content-display img {
          max-width: 100% !important;
          height: auto !important;
          margin-top: 1.5rem !important;
          margin-bottom: 1.5rem !important;
          border-radius: 0.75rem !important;
          display: inline-block;
        }
        
        /* Support for TipTap image alignment styles */
        .text-content-display img[style*="margin-left: auto"][style*="margin-right: auto"],
        .text-content-display img[style*="margin-left:auto"][style*="margin-right:auto"] {
          display: block !important;
          margin-left: auto !important;
          margin-right: auto !important;
        }

        .text-content-display p[style*="text-align: center"],
        .text-content-display div[style*="text-align: center"],
        .text-content-display [style*="text-align:center"] {
          text-align: center !important;
        }
        
        .text-content-display p[style*="text-align: center"] img,
        .text-content-display div[style*="text-align: center"] img {
          display: block !important;
          margin-left: auto !important;
          margin-right: auto !important;
        }

        /* Paragraph Spacing */
        .text-content-display p {
          margin-bottom: 1.25rem !important;
          line-height: 1.7 !important;
        }
        .text-content-display p:last-child {
          margin-bottom: 0 !important;
        }

        /* Code Blocks */
        .text-content-display pre {
          background: #1e293b !important;
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
          border-radius: 0.75rem !important;
          padding: 1.25rem !important;
          margin: 1.5rem 0 !important;
          overflow-x: auto !important;
        }
        .text-content-display pre code {
          background: transparent !important;
          color: #f8fafc !important;
          padding: 0 !important;
          font-size: 0.875rem !important;
          line-height: 1.7 !important;
          display: block !important;
          white-space: pre !important;
          font-family: monospace !important;
        }
        .text-content-display :not(pre) > code {
          background: rgba(204, 0, 1, 0.1) !important;
          color: #CC0001 !important;
          padding: 0.2rem 0.4rem !important;
          border-radius: 0.4rem !important;
          font-family: monospace !important;
          font-size: 0.875em !important;
        }
      `}</style>
    </>
  )
}

export default TextContent
